import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { ChromePicker, ColorResult, SketchPicker } from 'react-color';
import OutsideClickHandler from 'react-outside-click-handler';
import cns from 'classnames';
import uniqueId from 'lodash/uniqueId';
import Icon from '../../../Icon';
import { Props } from './types';
import NewsPng from '../../../../assets/img/news2.png';
import { getAllStyle} from '../../../../stores/reducers/sections';

const Input: Props = ({
  className,
  label,
  aside,
  icon,
  onIconClick,
  value,
  info,
  hex,
  inputRef,
  handleChange,
  type = 'text',
  colorType = 'hex',
  isRedifined,
  handlerReset,
  ...props
}) => {
  const [inputValue, setInputValue] = useState(value ?? '');
  const [inputColor, setInputColor] = useState<any>(hex);
  const [inputColorRgb, setInputColorRgb] = useState<any>();
  const [displayColorPicker, setDisplayColorPicker] = useState<boolean>(false);
  const styles = useSelector(getAllStyle);
  const defaultColors = (Object.values(styles.colors) as string[]).map((color: string): string => color);

  // const hex2rgba = (hex: any, alpha = 1) => {
  //   const [r, g, b] = hex.match(/\w\w/g).map((x: string) => parseInt(x, 16));
  //   return {r: r as number, g: g as number, b: b as number, a: 1,};
  // };

  const rgb2hex = (rgba: any) => {
    const rgb = rgba.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d+(?:\.\d+)?))?\)$/i);
    return (rgb && rgb.length === 5) ? "#" +
     ("0" + parseInt(rgb[1],10).toString(16)).slice(-2) +
     ("0" + parseInt(rgb[2],10).toString(16)).slice(-2) +
     ("0" + parseInt(rgb[3],10).toString(16)).slice(-2) : '';
   }

  const parseRgba = (rgba: any) => {
    const keys = ['r', 'g', 'b', 'a'];
    const rgbaArr = rgba.replace(/[^\d,]/g, '').split(',');
    let result = keys.reduce((acc: any, item, index) => {
      acc[item] = rgbaArr[index];
      return acc;
    }, {});
    return result;
  }

  const convertRgbToString = (rgb: any) => {
    const result = Object.values(rgb).map((val) => val).join(', ');
    return `rgba(${result})`;
  };

  useEffect(() => {
    if (value) {
      if (colorType !== 'rgb') {
        setInputColor(value.toString());
      } else {
        if (value.toString().indexOf('#') !== -1) {
          setInputColor(value.toString());
        } else {
          setInputColor(rgb2hex(value));
          setInputColorRgb(parseRgba(value));
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    if (hex) {
      if (colorType !== 'rgb') {
        setInputColor(hex.toString());
      } else {
        if (hex.toString().indexOf('#') !== -1) {
          setInputColor(hex.toString());
        } else {
          setInputColor(rgb2hex(hex));
          setInputColorRgb(parseRgba(hex));
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hex]);

  const id = useMemo(() => {
    return uniqueId();
  }, []);

  const onInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (colorType !== 'rgb') {
        setInputValue(e.target.value);
        if (handleChange) {
          handleChange(e.target.value);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleChange]
  );

  const onPickerChange = useCallback(
    (color: ColorResult) => {
      if (colorType !== 'rgb') {
        setInputColor(color.hex);
      } else {
        // setInputColor(color.hex);
        setInputColor(rgb2hex(convertRgbToString(color.rgb)));
        setInputColorRgb(color.rgb);
      }

      if (handleChange) {
        if (colorType !== 'rgb') {
          handleChange(color.hex);
        } else {
          handleChange(convertRgbToString(color.rgb));
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleChange]
  );

  const onFocus = useCallback(() => {
    setDisplayColorPicker(true);
  }, []);

  const onBlur = useCallback(() => {
    // setDisplayColorPicker(false);
  }, []);

  const handleIconClick = useCallback(() => {
    if (onIconClick) {
      onIconClick();
    }
  }, [onIconClick]);

  const inputComponent = useMemo(() => {
    switch (aside) {
      case 'color':
        return (
          <>
            <input
              {...props}
              readOnly
              id={id}
              ref={inputRef}
              className={cns('ui-input', className)}
              type={type}
              value={inputColor ? inputColor : inputColorRgb}
              onChange={onInputChange}
              onFocus={onFocus}
              onBlur={onBlur}
            />
            {displayColorPicker && (
              <OutsideClickHandler
                onOutsideClick={() => {
                  setDisplayColorPicker(false);
                }}>
                {
                  colorType !== 'rgb' ?
                  (<div className={styles.picker}>
                    <ChromePicker className="color-picker" color={inputColor} onChange={onPickerChange} />
                  </div>)
                  :
                  (<div className={styles.picker}>
                    <SketchPicker className="color-picker" color={inputColorRgb} onChange={onPickerChange} presetColors={defaultColors} />
                  </div>)
                }
              </OutsideClickHandler>
            )}
          </>
        );

      default:
        return (
          <input
            {...props}
            id={id}
            ref={inputRef}
            className={cns('ui-input', className)}
            type={type}
            value={value}
            onChange={onInputChange}
            disabled={colorType === 'rgb'}
          />
        );
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    aside,
    inputValue,
    inputColor,
    displayColorPicker,
    className,
    id,
    inputRef,
    onBlur,
    onFocus,
    onInputChange,
    onPickerChange,
    props,
    type,
    isRedifined,
    handlerReset,
  ]);

  const asideComponent = useMemo(() => {
    switch (aside) {
      case 'image':
        return (
          <div className="setting-field-aside">
            <div className="setting-round-bl">
              <img src={NewsPng} alt="" />
            </div>
          </div>
        );
      case 'color':
        return (
          <div className="setting-field-aside">
            <div
              className="setting-round-bl"
              style={{
                backgroundColor: inputColor,
              }}
            />
            {
              isRedifined &&
              <button className="setting-reset-color" title="Сбросить цвет" onClick={handlerReset}>
                <Icon name="close" />
              </button>
            }
          </div>
        );
      default:
        return null;
    }
  }, [aside, inputColor, isRedifined, handlerReset]);

  return (
    <div className="ui-group">
      <div className="setting-label">{label}</div>
      <div className="setting-field-group">
        <div className="setting-field">
          {icon && (
            <button type="button" className="setting-field-btn" onClick={handleIconClick}>
              <Icon name={icon} />
            </button>
          )}
          {inputComponent}
          {info && <span className="setting-field__info">{info}</span>}
        </div>

        {asideComponent}
      </div>
    </div>
  );
};

export default Input;
